import React from 'react';
import type { QuoteFormData, QuoteFormErrors } from '../../types/quote';
import { MuiDatePickerField, MuiTextInput } from './MuiInputs';
import { Checkbox, FormControlLabel, Box } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface StepDeliveryPreferenceProps {
  data: QuoteFormData;
  errors: QuoteFormErrors;
  onChange: (field: keyof QuoteFormData, value: any) => void;
}

const SPECIAL_HANDLING_OPTIONS = [
  { id: 'Fragile', label: 'Fragile', desc: 'Glass, electronics, ceramics, delicate items' },
  { id: 'Heavy / Oversized', label: 'Heavy / Oversized', desc: 'Requires two-person lift or special equipment' },
  { id: 'Handle With Care', label: 'Handle With Care', desc: 'High priority, upright transit, no stacking' },
  { id: 'Other', label: 'Other', desc: 'Custom handling specifications' },
];

const TIME_PRESETS = [
  'Morning (8:00 AM – 12:00 PM)',
  'Afternoon (12:00 PM – 5:00 PM)',
  'Evening (5:00 PM – 8:00 PM)',
  'Flexible / Any Time',
];

export const StepDeliveryPreference: React.FC<StepDeliveryPreferenceProps> = ({
  data,
  errors,
  onChange,
}) => {
  const handleToggleHandling = (option: string) => {
    const current = data.specialHandling || [];
    if (current.includes(option)) {
      onChange('specialHandling', current.filter((item) => item !== option));
      if (option === 'Other') {
        onChange('otherHandlingDetails', '');
      }
    } else {
      onChange('specialHandling', [...current, option]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-100 pb-4">
        <span className="text-xs uppercase tracking-widest text-[#F55109] font-bold">
          Step 5 of 6
        </span>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] special-header mt-1">
          SECTION 5 — DELIVERY PREFERENCE
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Specify your schedule preferences and special handling requirements.
        </p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          {/* Preferred Pickup Date (MUI DatePicker) */}
          <MuiDatePickerField
            label="Preferred Pickup Date"
            value={data.preferredPickupDate}
            onChange={(dateStr) => onChange('preferredPickupDate', dateStr)}
            required
            errorMessage={errors.preferredPickupDate}
          />

          {/* Preferred Pickup Time (MUI Text Input) */}
          <div>
            <MuiTextInput
              id="preferredPickupTime"
              label="Preferred Pickup Time (Optional)"
              value={data.preferredPickupTime}
              onChange={(val) => onChange('preferredPickupTime', val)}
              placeholder="e.g. 10:00 AM or select preset below"
              startIcon={<AccessTimeIcon fontSize="small" />}
            />

            {/* Quick time selection presets */}
            <div className="flex flex-wrap gap-2 mt-2">
              {TIME_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onChange('preferredPickupTime', preset)}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
                    data.preferredPickupTime === preset
                      ? 'bg-[#F55109] text-white border-[#F55109] font-medium shadow-sm'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-600 border-gray-200'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Does the item require special handling? Choice — Multiple Answers — Optional */}
        <div className="pt-2">
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Does the item require special handling?{' '}
            <span className="text-xs font-normal text-gray-500">
              (Choice — Multiple Answers — Optional)
            </span>
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Select all that apply to ensure we allocate the appropriate transport vehicle and equipment.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SPECIAL_HANDLING_OPTIONS.map((opt) => {
              const isChecked = (data.specialHandling || []).includes(opt.id);
              return (
                <Box
                  key={opt.id}
                  onClick={() => handleToggleHandling(opt.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    p: 2,
                    borderRadius: '14px',
                    border: '1.5px solid',
                    borderColor: isChecked ? '#F55109' : '#E5E7EB',
                    backgroundColor: isChecked ? 'rgba(245, 81, 9, 0.04)' : '#F9FAFB',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: isChecked ? '#F55109' : '#D1D5DB',
                      backgroundColor: isChecked ? 'rgba(245, 81, 9, 0.06)' : '#FFFFFF',
                    },
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleToggleHandling(opt.id)}
                    sx={{
                      p: 0,
                      mr: 1.5,
                      mt: 0.25,
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#F55109',
                      },
                    }}
                  />
                  <div>
                    <span className="block text-sm font-bold text-gray-900">{opt.label}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{opt.desc}</span>
                  </div>
                </Box>
              );
            })}
          </div>

          {/* Conditional field when "Other" is checked */}
          {(data.specialHandling || []).includes('Other') && (
            <div className="mt-4 p-4 bg-orange-50/50 border border-orange-200 rounded-2xl">
              <MuiTextInput
                id="otherHandlingDetails"
                label="Please specify other handling requirements"
                value={data.otherHandlingDetails}
                onChange={(val) => onChange('otherHandlingDetails', val)}
                placeholder="e.g. Temperature-controlled / keep between 15-20°C"
                startIcon={<WarningAmberIcon fontSize="small" sx={{ color: '#F55109' }} />}
              />
            </div>
          )}
        </div>

        {/* Additional Handling Instructions (MUI Long Answer - Optional) */}
        <MuiTextInput
          id="additionalHandlingInstructions"
          label="Additional Handling Instructions (Long Answer — Optional)"
          multiline
          rows={3}
          value={data.additionalHandlingInstructions}
          onChange={(val) => onChange('additionalHandlingInstructions', val)}
          placeholder="Any other specific loading, unloading, temperature, security, or handling requirements"
        />
      </div>
    </div>
  );
};
